import { useState, useRef, useCallback, useEffect, useMemo, type KeyboardEvent } from 'react'
import { MsgType, RelationType } from 'matrix-js-sdk'
import { requireClient } from '@/lib/matrix/client'
import { sendTyping, stopTyping } from '@/lib/matrix/typing'
import { useAuthStore } from '@/lib/store/auth-store'
import { buildTextContent, getMentionQuery, renderMentions, type Mention } from '@/lib/utils/mentions'
import { replaceShortcodes, shortcodeAtCaret, getShortcodeQuery, searchShortcodes, type ShortcodeMatch } from '@/lib/utils/emoji'
import { Avatar } from '@/components/ui/Avatar'
import { useToastStore } from '@/components/ui/Toast'
import { ReplyComposerBar } from './ReplyComposerBar'
import { FileUploadButton } from './FileUploadButton'
import { UploadPreview } from './UploadPreview'
import { EmojiPicker } from './EmojiPicker'
import { GifPicker, type Gif } from './GifPicker'
import type { SerializedEvent } from '@/lib/matrix/events'

interface MessageComposerProps {
  roomId: string
  replyTo: SerializedEvent | null
  editingEvent: SerializedEvent | null
  droppedFiles?: File[]
  /** When set, messages are sent into this thread (m.thread relation). */
  threadRootId?: string
  onCancelReply: () => void
  onCancelEdit: () => void
  /** Slack-style: ↑ in an empty composer edits your most recent message. */
  onEditLast?: () => void
  onDroppedFilesConsumed?: () => void
  /** The room's main composer. Owns auto-focus + type-to-focus (so it doesn't
   *  fight the thread-panel composer over focus). */
  primary?: boolean
}

export function MessageComposer({ roomId, replyTo, editingEvent, droppedFiles, threadRootId, onCancelReply, onCancelEdit, onEditLast, onDroppedFilesConsumed, primary }: MessageComposerProps) {
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [files, setFiles] = useState<File[]>([])

  useEffect(() => {
    if (droppedFiles && droppedFiles.length > 0) {
      setFiles((prev) => [...prev, ...droppedFiles])
      onDroppedFilesConsumed?.()
    }
  }, [droppedFiles, onDroppedFilesConsumed])
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    // Cap matches the .slk-textarea max-height in redesign.css; past it the
    // textarea scrolls internally instead of growing over the timeline.
    el.style.height = Math.min(el.scrollHeight, 200) + 'px'
  }, [])

  // Load the message's current text into the box when entering edit mode (Slack
  // style), so it can be tweaked in place rather than retyped. Strips any reply
  // fallback quote lines. Clearing the box on exit keeps cancel/send tidy.
  useEffect(() => {
    if (!editingEvent) {
      setMessage('')
      return
    }
    const raw = typeof editingEvent.content.body === 'string' ? editingEvent.content.body : ''
    let body = raw
    if (raw.startsWith('> ')) {
      const i = raw.indexOf('\n\n')
      if (i !== -1) body = raw.slice(i + 2)
    }
    setMessage(body)
    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (!el) return
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
      adjustHeight()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingEvent?.eventId])

  // The composer is the default place for keystrokes to go: focus it on room
  // open and when starting a reply, and redirect stray typing back to it. We only
  // yield focus when something is deliberately focused (another field, a button)
  // or a modal is open — there's no reason to sit unfocused otherwise.
  const modalOpen = () => !!document.querySelector('.fixed.inset-0.z-50')

  useEffect(() => {
    // Skip on touch-only devices so switching rooms doesn't pop the keyboard.
    if (!primary || modalOpen() || !window.matchMedia('(pointer: fine)').matches) return
    textareaRef.current?.focus()
  }, [primary, roomId])

  useEffect(() => {
    if (primary && replyTo) textareaRef.current?.focus()
  }, [primary, replyTo])

  useEffect(() => {
    if (!primary) return
    const onDocKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return // shortcuts / non-printable
      const el = textareaRef.current
      if (!el || document.activeElement === el) return
      const active = document.activeElement as HTMLElement | null
      if (active && active !== document.body) {
        const tag = active.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active.isContentEditable) return
      }
      if (modalOpen()) return
      el.focus() // the keystroke lands in the now-focused textarea
    }
    document.addEventListener('keydown', onDocKeyDown)
    return () => document.removeEventListener('keydown', onDocKeyDown)
  }, [primary])

  // @-mention autocomplete. Candidates come straight from the live room (the
  // member store is only populated lazily when the member panel is opened).
  const myUserId = useAuthStore((s) => s.userId)
  const [mentions, setMentions] = useState<Mention[]>([])
  const [mentionState, setMentionState] = useState<{ start: number; query: string } | null>(null)
  const [mentionIndex, setMentionIndex] = useState(0)
  const pendingCaretRef = useRef<number | null>(null)

  const mentionMatches = useMemo(() => {
    if (!mentionState) return []
    const room = requireClient().getRoom(roomId)
    if (!room) return []
    const q = mentionState.query.toLowerCase()
    return room
      .getJoinedMembers()
      .filter((m) => m.userId !== myUserId)
      .map((m) => ({ userId: m.userId, displayName: m.name || m.userId, avatarUrl: m.getMxcAvatarUrl() ?? null }))
      .filter((m) => m.displayName.toLowerCase().includes(q) || m.userId.toLowerCase().includes(q))
      .slice(0, 8)
  }, [mentionState, roomId, myUserId])

  const refreshMentionQuery = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    const ctx = getMentionQuery(el.value, el.selectionStart ?? el.value.length)
    setMentionState(ctx)
    setMentionIndex(0)
  }, [])

  // Emoji shortcode autocomplete (`:smi` → suggestions), mirroring mentions.
  const [shortcodeState, setShortcodeState] = useState<{ start: number; query: string } | null>(null)
  const [shortcodeIndex, setShortcodeIndex] = useState(0)

  const shortcodeMatches = useMemo<ShortcodeMatch[]>(
    () => (shortcodeState ? searchShortcodes(shortcodeState.query) : []),
    [shortcodeState]
  )

  const refreshShortcodeQuery = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    setShortcodeState(getShortcodeQuery(el.value, el.selectionStart ?? el.value.length))
    setShortcodeIndex(0)
  }, [])

  const applyShortcode = useCallback((m: ShortcodeMatch) => {
    const el = textareaRef.current
    if (!el || !shortcodeState) return
    const caret = el.selectionStart ?? message.length
    const before = message.slice(0, shortcodeState.start)
    const after = message.slice(caret)
    setMessage(before + m.native + after)
    setShortcodeState(null)
    pendingCaretRef.current = before.length + m.native.length
  }, [message, shortcodeState])

  const applyMention = useCallback((m: { userId: string; displayName: string }) => {
    const el = textareaRef.current
    if (!el || !mentionState) return
    const caret = el.selectionStart ?? message.length
    const before = message.slice(0, mentionState.start)
    const after = message.slice(caret)
    const inserted = `@${m.displayName} `
    const next = before + inserted + after
    setMentions((prev) => [...prev, { id: m.userId, display: m.displayName }])
    setMessage(next)
    setMentionState(null)
    pendingCaretRef.current = before.length + inserted.length
  }, [message, mentionState])

  // Restore caret after a mention insertion re-renders the textarea.
  useEffect(() => {
    if (pendingCaretRef.current === null) return
    const el = textareaRef.current
    if (el) {
      el.focus()
      el.setSelectionRange(pendingCaretRef.current, pendingCaretRef.current)
    }
    pendingCaretRef.current = null
    adjustHeight()
  }, [message, adjustHeight])

  // Relation that routes a message into the active thread (and a reply fallback
  // so non-threaded clients still render it sensibly).
  const threadRelation = threadRootId
    ? {
        'm.relates_to': {
          rel_type: RelationType.Thread,
          event_id: threadRootId,
          is_falling_back: true,
          'm.in_reply_to': { event_id: threadRootId },
        },
      }
    : {}

  const uploadFile = useCallback(async (file: File, caption?: string) => {
    const client = requireClient()
    const upload = await client.uploadContent(file, { type: file.type })
    const url = upload.content_uri

    let msgtype: MsgType = MsgType.File
    if (file.type.startsWith('image/')) msgtype = MsgType.Image
    else if (file.type.startsWith('video/')) msgtype = MsgType.Video
    else if (file.type.startsWith('audio/')) msgtype = MsgType.Audio

    // Per the Matrix spec (MSC2530), a media caption is only recognised when
    // `filename` is set and differs from `body`; otherwise `body` is taken as
    // the filename. Always send `filename` so bridges (e.g. mautrix-signal)
    // treat the caption as a caption instead of dropping it.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await client.sendMessage(roomId, {
      msgtype,
      body: caption || file.name,
      filename: file.name,
      url,
      info: {
        mimetype: file.type,
        size: file.size,
      },
      ...threadRelation,
    } as any)
  }, [roomId, threadRootId])

  const sendMessage = useCallback(async () => {
    const text = replaceShortcodes(message.trim())
    const outgoingFiles = files
    if (!text && outgoingFiles.length === 0) return
    if (sending) return

    const outgoingMentions = mentions
    const wasEditing = editingEvent
    const wasReplyTo = replyTo

    // Clear the composer immediately. The message appears in the timeline via
    // local echo, so it shouldn't linger in the box while a (possibly slow,
    // encrypted) send is in flight. Restored below if the send fails.
    setSending(true)
    setMessage('')
    setMentions([])
    setFiles([])
    stopTyping(roomId)
    if (textareaRef.current) textareaRef.current.style.height = 'auto'

    try {
      const client = requireClient()

      if (outgoingFiles.length > 0) {
        if (outgoingFiles.length === 1 && text && !wasEditing && !wasReplyTo) {
          await uploadFile(outgoingFiles[0]!, text)
          return
        }
        for (const file of outgoingFiles) {
          await uploadFile(file)
        }
      }

      if (text) {
        if (wasEditing) {
          const newContent = buildTextContent(text, outgoingMentions)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const top: any = { msgtype: MsgType.Text, body: `* ${text}` }
          if (newContent.formatted_body) {
            top.format = 'org.matrix.custom.html'
            top.formatted_body = `* ${newContent.formatted_body}`
          }
          if (newContent['m.mentions']) top['m.mentions'] = newContent['m.mentions']
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (client as any).sendEvent(roomId, 'm.room.message', {
            ...top,
            'm.new_content': newContent,
            'm.relates_to': {
              rel_type: RelationType.Replace,
              event_id: wasEditing.eventId,
            },
          })
          onCancelEdit()
        } else if (wasReplyTo) {
          const { html: bodyHtml, ids } = renderMentions(text, outgoingMentions)
          await client.sendMessage(roomId, {
            msgtype: MsgType.Text,
            body: `> <${wasReplyTo.sender}> ${(wasReplyTo.content.body as string) ?? ''}\n\n${text}`,
            format: 'org.matrix.custom.html',
            formatted_body: `<mx-reply><blockquote><a href="https://matrix.to/#/${roomId}/${wasReplyTo.eventId}">In reply to</a> <a href="https://matrix.to/#/${wasReplyTo.sender}">${wasReplyTo.sender}</a><br>${(wasReplyTo.content.body as string) ?? ''}</blockquote></mx-reply>${bodyHtml}`,
            'm.relates_to': {
              'm.in_reply_to': {
                event_id: wasReplyTo.eventId,
              },
            },
            'm.mentions': { user_ids: [...new Set([wasReplyTo.sender, ...ids])] },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any)
          onCancelReply()
        } else if (threadRootId) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await client.sendMessage(roomId, { ...buildTextContent(text, outgoingMentions), ...threadRelation } as any)
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await client.sendMessage(roomId, buildTextContent(text, outgoingMentions) as any)
        }
      }
    } catch (err) {
      console.error('Failed to send:', err)
      // Restore what we cleared so the message isn't lost.
      setMessage(text)
      setMentions(outgoingMentions)
      setFiles(outgoingFiles)
      useToastStore.getState().addToast('Failed to send message', 'error')
    } finally {
      setSending(false)
      textareaRef.current?.focus()
    }
  }, [message, mentions, roomId, sending, files, replyTo, editingEvent, threadRootId, uploadFile, onCancelReply, onCancelEdit])

  const handleKeyDown = (e: KeyboardEvent) => {
    if (mentionState && mentionMatches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionIndex((i) => (i + 1) % mentionMatches.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        applyMention(mentionMatches[mentionIndex]!)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMentionState(null)
        return
      }
    }
    if (shortcodeState && shortcodeMatches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setShortcodeIndex((i) => (i + 1) % shortcodeMatches.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setShortcodeIndex((i) => (i - 1 + shortcodeMatches.length) % shortcodeMatches.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        applyShortcode(shortcodeMatches[shortcodeIndex]!)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setShortcodeState(null)
        return
      }
    }
    // Slack-style: ↑ in an empty composer jumps to editing your last message.
    if (
      e.key === 'ArrowUp' &&
      onEditLast &&
      !editingEvent &&
      !replyTo &&
      !mentionState &&
      files.length === 0 &&
      message.trim() === ''
    ) {
      e.preventDefault()
      onEditLast()
      return
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
    if (e.key === 'Escape') {
      if (editingEvent) onCancelEdit()
      if (replyTo) onCancelReply()
    }
  }

  const handleEmojiSelect = (emoji: string) => {
    // Keep the picker open so multiple emoji can be added in one go (Slack/
    // Discord style); it dismisses on click-outside, the toggle button, or Esc.
    // Don't steal focus back to the textarea per pick — focus stays in the
    // picker while it's open and returns to the composer naturally on close.
    setMessage((prev) => prev + emoji)
  }

  const [showGif, setShowGif] = useState(false)
  // Fetch the GIF bytes (server-proxied to dodge CORS), upload to our homeserver,
  // and send as m.image/image-gif so it animates for us and bridges as a GIF.
  const sendGif = useCallback(async (gif: Gif) => {
    setShowGif(false)
    try {
      const res = await fetch(`/api/image-proxy?url=${encodeURIComponent(gif.gif)}`)
      if (!res.ok) throw new Error('fetch failed')
      const blob = await res.blob()
      const client = requireClient()
      const upload = await client.uploadContent(new File([blob], 'giphy.gif', { type: 'image/gif' }), { type: 'image/gif' })
      await client.sendMessage(roomId, {
        msgtype: MsgType.Image,
        body: 'GIF',
        url: upload.content_uri,
        info: { mimetype: 'image/gif', size: blob.size, w: gif.w, h: gif.h },
        ...threadRelation,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any)
    } catch {
      useToastStore.getState().addToast('Failed to send GIF', 'error')
    }
  }, [roomId, threadRootId])

  const [locating, setLocating] = useState(false)
  const sendLocation = useCallback(() => {
    if (!navigator.geolocation) {
      useToastStore.getState().addToast('Geolocation is not available', 'error')
      return
    }
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords
        const client = requireClient()
        client
          .sendMessage(roomId, {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            msgtype: 'm.location' as any,
            body: `Location: ${lat.toFixed(5)}, ${lng.toFixed(5)}`,
            geo_uri: `geo:${lat},${lng}`,
            'org.matrix.msc3488.location': { uri: `geo:${lat},${lng}` },
            ...threadRelation,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any)
          .catch(() => useToastStore.getState().addToast('Failed to send location', 'error'))
          .finally(() => setLocating(false))
      },
      () => {
        useToastStore.getState().addToast('Could not get your location', 'error')
        setLocating(false)
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }, [roomId, threadRootId])

  const roomName = requireClient().getRoom(roomId)?.name ?? ''
  const canSend = !!message.trim() || files.length > 0

  // Format toolbar — wrap the current selection with markdown markers.
  const wrapSelection = (before: string, after = before) => {
    const el = textareaRef.current
    const s = el?.selectionStart ?? message.length
    const e = el?.selectionEnd ?? s
    const sel = message.slice(s, e)
    setMessage(message.slice(0, s) + before + sel + after + message.slice(e))
    pendingCaretRef.current = s + before.length + sel.length + after.length
  }

  return (
    <div className="composer-wrap">
      {replyTo && (
        <ReplyComposerBar event={replyTo} onDismiss={onCancelReply} />
      )}
      {editingEvent && (
        <div className="composer-reply">
          <svg className="shrink-0 text-[rgb(var(--np-accent))]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          <span className="text-xs text-[rgb(var(--np-tx-muted))] flex-1">Editing message</span>
          <button onClick={onCancelEdit} className="icon-btn-sm" title="Cancel">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
      )}
      <UploadPreview
        files={files}
        onRemove={(i) => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
      />
      <div className="slk-composer">
        <div className="slk-format">
          <button className="slk-fmt-btn" title="Bold" onClick={() => wrapSelection('**')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h8a4 4 0 0 1 0 8H6z" /><path d="M6 12h9a4 4 0 0 1 0 8H6z" /></svg>
          </button>
          <button className="slk-fmt-btn" title="Italic" onClick={() => wrapSelection('_')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="4" x2="10" y2="4" /><line x1="14" y1="20" x2="5" y2="20" /><line x1="15" y1="4" x2="9" y2="20" /></svg>
          </button>
          <button className="slk-fmt-btn" title="Code" onClick={() => wrapSelection('`')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>
          </button>
          <button className="slk-fmt-btn" title="Link" onClick={() => wrapSelection('[', '](url)')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
          </button>
          <button className="slk-fmt-btn" title="List" onClick={() => wrapSelection('\n- ', '')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
          </button>
        </div>

        <div className="relative">
          {mentionState && mentionMatches.length > 0 && (
            <div className="absolute bottom-full mb-2 left-0 w-72 max-h-60 overflow-y-auto bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg shadow-lg py-1 z-20">
              {mentionMatches.map((m, i) => (
                <button
                  key={m.userId}
                  onMouseDown={(e) => { e.preventDefault(); applyMention(m) }}
                  onMouseEnter={() => setMentionIndex(i)}
                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors ${
                    i === mentionIndex ? 'bg-[rgb(var(--np-raised))]' : 'hover:bg-[rgb(var(--np-raised)/0.5)]'
                  }`}
                >
                  <Avatar mxc={m.avatarUrl} name={m.displayName} userId={m.userId} size={24} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-[rgb(var(--np-tx-primary))] truncate">{m.displayName}</span>
                    <span className="block text-[10px] font-data text-[rgb(var(--np-tx-muted))] truncate">{m.userId}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {shortcodeState && shortcodeMatches.length > 0 && (
            <div className="absolute bottom-full mb-2 left-0 w-72 max-h-60 overflow-y-auto bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg shadow-lg py-1 z-20">
              {shortcodeMatches.map((m, i) => (
                <button
                  key={m.code}
                  onMouseDown={(e) => { e.preventDefault(); applyShortcode(m) }}
                  onMouseEnter={() => setShortcodeIndex(i)}
                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors ${
                    i === shortcodeIndex ? 'bg-[rgb(var(--np-raised))]' : 'hover:bg-[rgb(var(--np-raised)/0.5)]'
                  }`}
                >
                  <span className="text-xl leading-none w-6 text-center shrink-0">{m.native}</span>
                  <span className="text-sm font-data text-[rgb(var(--np-tx-secondary))] truncate">:{m.code}:</span>
                </button>
              ))}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => {
              const el = e.currentTarget
              // Convert a just-completed :shortcode: into its emoji in place.
              const sc = shortcodeAtCaret(el.value, el.selectionStart ?? el.value.length)
              if (sc) {
                setMessage(sc.text)
                pendingCaretRef.current = sc.caret
                setShortcodeState(null)
              } else {
                setMessage(el.value)
                refreshShortcodeQuery()
              }
              adjustHeight()
              refreshMentionQuery()
              if (el.value.trim()) sendTyping(roomId)
            }}
            onClick={() => { refreshMentionQuery(); refreshShortcodeQuery() }}
            onKeyUp={(e) => {
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
                refreshMentionQuery()
                refreshShortcodeQuery()
              }
            }}
            onKeyDown={handleKeyDown}
            onPaste={(e) => {
              const items = Array.from(e.clipboardData.items)
              const imageItems = items.filter((item) => item.type.startsWith('image/'))
              if (imageItems.length > 0) {
                e.preventDefault()
                const newFiles = imageItems.map((item) => item.getAsFile()).filter((f): f is File => f !== null)
                if (newFiles.length > 0) setFiles((prev) => [...prev, ...newFiles])
                return
              }
              // Wrap a big text paste in a code fence so it sends as a collapsed
              // block instead of flooding the timeline. Skip if it's already fenced.
              const pasted = e.clipboardData.getData('text/plain')
              if (pasted && !pasted.includes('```') && (pasted.split('\n').length > 10 || pasted.length > 600)) {
                e.preventDefault()
                const el = e.currentTarget
                const start = el.selectionStart ?? message.length
                const end = el.selectionEnd ?? start
                const before = message.slice(0, start)
                const after = message.slice(end)
                const block = `${before && !before.endsWith('\n') ? '\n' : ''}\`\`\`\n${pasted}\n\`\`\`\n`
                setMessage(before + block + after)
                pendingCaretRef.current = (before + block).length
              }
            }}
            placeholder={editingEvent ? 'Edit your message…' : `Message ${roomName}`}
            rows={1}
            className="slk-textarea"
          />
        </div>

        <div className="slk-actions">
          <div className="flex" style={{ gap: 1 }}>
            <FileUploadButton
              onFilesSelected={(newFiles) => setFiles((prev) => [...prev, ...newFiles])}
              disabled={sending}
              className="composer-btn slk-act"
            />
            <button className="composer-btn slk-act" title="Share location" onClick={sendLocation} disabled={locating}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
              </svg>
            </button>
            <div className="relative">
              <button className="composer-btn slk-act" title="Emoji" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
                </svg>
              </button>
              {showEmojiPicker && (
                <EmojiPicker onSelect={handleEmojiSelect} onClose={() => setShowEmojiPicker(false)} />
              )}
            </div>
            <div className="relative">
              <button className="composer-btn slk-act" title="GIF" onClick={() => setShowGif(!showGif)} style={{ width: 'auto', padding: '0 6px' }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.03em', border: '1.5px solid currentColor', borderRadius: 4, padding: '1px 3px', lineHeight: 1 }}>GIF</span>
              </button>
              {showGif && <GifPicker onSelect={sendGif} onClose={() => setShowGif(false)} />}
            </div>
          </div>
          <button
            onClick={sendMessage}
            disabled={!canSend || sending}
            className={`send-btn slk-send${canSend ? ' send-ready' : ''}`}
            title="Send"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
